import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = !loading && admin?.role === "superadmin";

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const fetchAdminAndPermissions = useCallback(async () => {
    try {
      const profileRes = await axios.get(`${BASE_URL}/api/admin/profile`, {
        withCredentials: true,
      });
      const adminData = profileRes.data;

      if (!adminData || !adminData.role) {
        console.warn("⏳ Waiting for admin data...");
        return;
      }

      setAdmin(adminData);

      const rolesRes = await axios.get(`${BASE_URL}/api/admin-roles`, {
        withCredentials: true,
      });

      const matchedRole = rolesRes.data.find((r) => r.name === adminData.role);
      if (!matchedRole) {
        console.warn("⚠️ Role not found for:", adminData.role);
      }

      setRolePermissions(matchedRole?.permissions || []);
    } catch (err) {
      console.error("🔴 Failed to fetch admin or roles:", err);
      setAdmin(null);
      setRolePermissions([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, setAdmin, setRolePermissions]);

  const canAccess = useCallback(
    (permission) => rolePermissions.includes(permission),
    [rolePermissions]
  );

  useEffect(() => {
    fetchAdminAndPermissions();
  }, [fetchAdminAndPermissions]);

  return (
    <AdminContext.Provider
      value={{
        admin,
        setAdmin,
        rolePermissions,
        canAccess,
        loading,
        isSuperAdmin
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
